import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Board, Template } from 'modules/core/domain/interfaces/supabase/overlay/entity';

import { Processes } from 'modules/template-editor/domain/interface/processes';
import {
  createAddLikeReducer,
  createAddViewReducer,
  createCopyBoardReducer,
  createCreateBoardReducer,
  createDeleteBoardReducer,
  createFetchBoardsByTemplateIdReducer,
  createFetchCatalogTemplatesReducer,
  createFetchTemplateWithBoardsReducer,
  createUpdateBoardReducer,
} from './reducers';

type Entity<T> = {
  byId: { [key: number]: T };
  allIds: number[];
};

export type TemplatesSliceState = {
  /**
   * Шаблоны, загруженные трансляцией (`fetchTemplateWithBoards`). Список шаблонов организации здесь не
   * хранится — он в кэше RTK Query (`getOwnerBoardTemplates`, IMGM-1566).
   */
  templates: Entity<Template>;
  boards: Entity<Board>;
  catalog: Entity<Template>;
  activeTemplateId?: number;
  activeBoardId?: number;
  loadedTemplates: number[];
  /** Шаблоны, чьи доски не удалось загрузить: редактору табло нечего ждать, показывать «не найдено». */
  failedTemplates: number[];

  isLoading: boolean;
  isLoaded: boolean;

  isLoadingBoards: boolean;
  isLoadedBoards: boolean;

  isProcessed: boolean;
  isFinished: boolean;
  process?: Processes;

  filterByName: string;
  sortByDate: 'none' | 'desc' | 'asc';
};

const initialState: TemplatesSliceState = {
  templates: {
    byId: {},
    allIds: [],
  },
  boards: {
    byId: {},
    allIds: [],
  },
  catalog: {
    byId: {},
    allIds: [],
  },

  loadedTemplates: [],
  failedTemplates: [],

  isLoaded: false,
  isLoading: false,

  isLoadingBoards: false,
  isLoadedBoards: false,

  isProcessed: false,
  isFinished: false,

  filterByName: '',
  sortByDate: 'none',
};

export const templatesSlice = createSlice({
  name: 'templates',
  initialState,
  reducers: {
    setActiveTemplate(state, action: PayloadAction<{ id: number | undefined }>) {
      if (action.payload.id && isNaN(action.payload.id)) {
        throw new Error('Invalid template id');
      }
      state.activeTemplateId = action.payload.id;
    },
    resetPressedState(state) {
      state.isProcessed = false;
      state.isFinished = false;
      state.process = undefined;
    },
    setActiveBoard(state, action: PayloadAction<{ id: number | undefined }>) {
      if (action.payload.id && isNaN(action.payload.id)) {
        throw new Error('Invalid board id');
      }
      state.activeBoardId = action.payload.id;
    },
    favoriteBoard(state, action: PayloadAction<{ id: number }>) {
      const board = state.boards.byId[action.payload.id];
      if (!board) return;
      board.isFavorite = !board.isFavorite;
    },
    setFilterByName: (state, action: PayloadAction<TemplatesSliceState['filterByName']>) => {
      state.filterByName = action.payload;
    },
    setSortByDate: (state, action: PayloadAction<TemplatesSliceState['sortByDate']>) => {
      state.sortByDate = action.payload;
    },
    setProcess(state, action: PayloadAction<TemplatesSliceState['process']>) {
      state.process = action.payload;
    },
    /**
     * Шаблон создан или изменён (мутации `board-templates-api.ts`). Список организации пишется в кэш RTK
     * Query там же; здесь — каталог (общие и публичные шаблоны) и копия, загруженная трансляцией.
     */
    boardTemplateSaved(state, action: PayloadAction<Template>) {
      const template = action.payload;
      if (state.templates.byId[template.id]) state.templates.byId[template.id] = template;

      const isInCatalog = template.access === 'public' || template.access === 'default';
      if (isInCatalog) {
        if (!state.catalog.byId[template.id]) state.catalog.allIds.push(template.id);
        state.catalog.byId[template.id] = template;
      } else {
        delete state.catalog.byId[template.id];
        state.catalog.allIds = state.catalog.allIds.filter((id) => id !== template.id);
      }
    },
    boardTemplateRemoved(state, action: PayloadAction<{ id: number }>) {
      const { id } = action.payload;
      delete state.templates.byId[id];
      state.templates.allIds = state.templates.allIds.filter((templateId) => templateId !== id);
      delete state.catalog.byId[id];
      state.catalog.allIds = state.catalog.allIds.filter((templateId) => templateId !== id);
      state.activeTemplateId = undefined;
    },
  },
  extraReducers: (builder) => {
    createCreateBoardReducer(builder);
    createDeleteBoardReducer(builder);
    createUpdateBoardReducer(builder);
    createCopyBoardReducer(builder);
    createFetchCatalogTemplatesReducer(builder);
    createAddLikeReducer(builder);
    createAddViewReducer(builder);
    createFetchBoardsByTemplateIdReducer(builder);
    createFetchTemplateWithBoardsReducer(builder);
  },
});

export const {
  setActiveTemplate,
  resetPressedState,
  setActiveBoard,
  favoriteBoard,
  setFilterByName,
  setSortByDate,
  setProcess,
  boardTemplateSaved,
  boardTemplateRemoved,
} = templatesSlice.actions;

export default templatesSlice.reducer;
